// components/common/Footer.jsx — Ink & Lime Design System
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Mail, Phone, MapPin } from 'lucide-react';

const T = {
  ink:'#0D0D0F', ink2:'#1A1A20', slate:'#2D2D38',
  muted:'#6B6B7A', white:'#FFFFFF', lime:'#C8F23C', teal:'#00BFA5',
};

const QUICK_LINKS = [
  {name:'Book a Bike',path:'/'},
  {name:'About Us',path:'/about-us'},
  {name:'Contact Us',path:'/contact-us'},
  {name:'FAQ',path:'/faq'},
  {name:'My Bookings',path:'/rental/search/my-bookings'},
];
const POLICIES = [
  {name:'Terms & Conditions',path:'/terms-and-conditions'},
  {name:'Refund Policy',path:'/refund-policy'},
  {name:'Return Policy',path:'/return-policy'},
  {name:'Shipping Policy',path:'/shipping-policy'},
];
const LOCATIONS = ['Kothrud Hub','Hinjewadi Tech Park','Wakad Central','Koregaon Park','Hadapsar Station'];

function FLink({path,name,accent}) {
  const [h,setH]=useState(false);
  return (
    <li>
      <Link to={path} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
        style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:13,fontWeight:500,
          color:h?accent:'rgba(255,255,255,.4)',textDecoration:'none',transition:'color .18s'}}>
        <span style={{color:accent,transition:'transform .18s',transform:h?'translateX(3px)':'none',display:'inline-block'}}>→</span>
        {name}
      </Link>
    </li>
  );
}

function ColHead({children}) {
  return (
    <h4 style={{fontSize:11,fontWeight:800,letterSpacing:1.8,textTransform:'uppercase',
      color:T.white,display:'flex',alignItems:'center',gap:8,marginBottom:18}}>
      <span style={{width:3,height:16,background:T.lime,borderRadius:4,display:'inline-block',flexShrink:0}}/>
      {children}
    </h4>
  );
}

const Footer = () => {
  const [open,setOpen]=useState('');
  return (
    <footer style={{background:T.ink,borderTop:`1px solid ${T.slate}`,fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Space+Grotesk:wght@700&display=swap');
        .vf * {box-sizing:border-box;}
        .vf a,.vf button {font-family:'DM Sans',sans-serif;}
        .vf-mobile-lnk {display:flex;align-items:center;gap:8px;padding:12px 0;
          color:rgba(255,255,255,.4);font-size:13px;font-weight:500;text-decoration:none;
          border:none;background:none;width:100%;cursor:pointer;transition:color .18s;}
        .vf-mobile-lnk:hover {color:#C8F23C;}
        @media(max-width:768px){.vf-desk{display:none!important;}.vf-mob{display:block!important;}}
        @media(min-width:769px){.vf-mob{display:none!important;}}
      `}</style>

      {/* lime top bar */}
      <div style={{height:2,background:T.lime}}/>

      <div className="vf" style={{maxWidth:1400,margin:'0 auto',padding:'52px 24px 40px'}}>

        {/* DESKTOP */}
        <div className="vf-desk" style={{display:'grid',gridTemplateColumns:'1.1fr .65fr .6fr .9fr',gap:48}}>

          {/* Brand */}
          <div>
            <Link to="/" style={{display:'inline-flex',alignItems:'center',gap:10,textDecoration:'none',marginBottom:16}}>
              <img src="/logo-vegatas.jpeg" alt="VEGATAS" style={{height:54,objectFit:'contain',filter:'brightness(1.15)'}}/>
            </Link>
            <p style={{fontSize:13,color:'rgba(255,255,255,.38)',lineHeight:1.75,marginBottom:24,maxWidth:270}}>
              Pune's most trusted two-wheeler rental. Premium bikes, seamless booking, and 24/7 support.
            </p>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {[
                {href:'tel:9730736858',icon:<Phone size={12} color={T.lime}/>,bg:'rgba(200,242,60,.1)',bd:'rgba(200,242,60,.2)',label:'+91 9730736858',hc:T.lime},
                {href:'mailto:nickhl.shinde@gmail.com',icon:<Mail size={12} color={T.teal}/>,bg:'rgba(0,191,165,.1)',bd:'rgba(0,191,165,.2)',label:'nickhl.shinde@gmail.com',hc:T.teal},
              ].map(({href,icon,bg,bd,label,hc})=>(
                <a key={href} href={href}
                  onMouseEnter={e=>e.currentTarget.style.color=hc}
                  onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,.4)'}
                  style={{display:'flex',alignItems:'center',gap:10,fontSize:13,color:'rgba(255,255,255,.4)',textDecoration:'none',transition:'color .18s'}}>
                  <span style={{width:26,height:26,borderRadius:8,background:bg,border:`1px solid ${bd}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    {icon}
                  </span>
                  {label}
                </a>
              ))}
              <div style={{display:'flex',alignItems:'flex-start',gap:10,fontSize:12,color:'rgba(255,255,255,.3)'}}>
                <span style={{width:26,height:26,borderRadius:8,background:'rgba(200,242,60,.06)',border:'1px solid rgba(200,242,60,.15)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>
                  <MapPin size={12} color={T.lime}/>
                </span>
                Suhas Society, Paud Rd, Kothrud, Pune 411038
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <ColHead>Quick Links</ColHead>
            <ul style={{listStyle:'none',display:'flex',flexDirection:'column',gap:10}}>
              {QUICK_LINKS.map(l=><FLink key={l.path} {...l} accent={T.lime}/>)}
            </ul>
          </div>

          {/* Policies */}
          <div>
            <ColHead>Policies</ColHead>
            <ul style={{listStyle:'none',display:'flex',flexDirection:'column',gap:10}}>
              {POLICIES.map(l=><FLink key={l.path} {...l} accent={T.teal}/>)}
            </ul>
          </div>

          {/* Pickup Hubs */}
          <div>
            <ColHead>Pickup Hubs</ColHead>
            <ul style={{listStyle:'none',display:'flex',flexDirection:'column',gap:10,marginBottom:24}}>
              {LOCATIONS.map(loc=>(
                <li key={loc} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'rgba(255,255,255,.38)'}}>
                  <MapPin size={12} color={T.muted} style={{flexShrink:0}}/>{loc}, Pune
                </li>
              ))}
            </ul>
            {/* Stats mini-card */}
            <div style={{padding:'14px 16px',background:T.ink2,borderRadius:12,border:`1px solid ${T.slate}`,display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {[['500+','Bikes'],['50K+','Riders'],['4.8★','Rating'],['24/7','Support']].map(([v,l])=>(
                <div key={l}>
                  <div style={{fontSize:15,fontWeight:900,color:T.lime}}>{v}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:1}}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* MOBILE */}
        <div className="vf-mob">
          {/* Brand */}
          <div style={{marginBottom:20,paddingBottom:20,borderBottom:`1px solid ${T.slate}`}}>
            <Link to="/" style={{display:'inline-flex',alignItems:'center',gap:8,textDecoration:'none',marginBottom:10}}>
              <img src="/logo-vegatas.jpeg" alt="VEGATAS" style={{height:44,objectFit:'contain',filter:'brightness(1.15)'}}/>
            </Link>
            <p style={{fontSize:12,color:'rgba(255,255,255,.35)',lineHeight:1.6,marginBottom:12}}>Pune's most trusted bike rental.</p>
            <a href="tel:9730736858" style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'rgba(255,255,255,.4)',textDecoration:'none',marginBottom:6}}>
              <Phone size={11} color={T.lime}/>+91 9730736858
            </a>
            <a href="mailto:nickhl.shinde@gmail.com" style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'rgba(255,255,255,.4)',textDecoration:'none'}}>
              <Mail size={11} color={T.teal}/>nickhl.shinde@gmail.com
            </a>
          </div>

          {[{key:'q',title:'Quick Links',links:QUICK_LINKS,accent:T.lime},{key:'p',title:'Policies',links:POLICIES,accent:T.teal}].map(({key,title,links,accent})=>(
            <div key={key} style={{borderBottom:`1px solid ${T.slate}`}}>
              <button onClick={()=>setOpen(o=>o===key?'':key)}
                style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',
                  padding:'14px 0',background:'none',border:'none',cursor:'pointer'}}>
                <span style={{fontSize:13,fontWeight:700,color:T.white}}>{title}</span>
                <ChevronDown size={15} color={open===key?T.lime:T.muted}
                  style={{transition:'transform .25s',transform:open===key?'rotate(180deg)':'none'}}/>
              </button>
              <div style={{overflow:'hidden',maxHeight:open===key?200:0,transition:'max-height .3s ease'}}>
                <ul style={{listStyle:'none',paddingBottom:14,display:'flex',flexDirection:'column',gap:8}}>
                  {links.map(l=><FLink key={l.path} {...l} accent={accent}/>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{borderTop:`1px solid ${T.slate}`}}>
        <div style={{maxWidth:1400,margin:'0 auto',padding:'14px 24px',display:'flex',flexWrap:'wrap',
          alignItems:'center',justifyContent:'space-between',gap:8}}>
          <p style={{fontSize:12,color:T.muted}}>
            © {new Date().getFullYear()} VEGATAS Bike Rental. All rights reserved. Legal name: NIKHIL SHINDE
          </p>
          <div style={{display:'flex',gap:16}}>
            {[['Terms','/terms-and-conditions'],['Privacy','/refund-policy'],['Support','/contact-us']].map(([l,p])=>(
              <Link key={p} to={p}
                onMouseEnter={e=>e.currentTarget.style.color=T.lime}
                onMouseLeave={e=>e.currentTarget.style.color=T.muted}
                style={{fontSize:12,color:T.muted,textDecoration:'none',transition:'color .18s'}}>{l}</Link>
            ))}
          </div>
        </div>
      </div>

      <div style={{display:'none'}} aria-hidden="true">
        Bike rental Pune, scooter rental Pune, VEGATAS bike rental,
        monthly bike rental Pune, bike rental Kothrud, bike rental Hinjewadi
      </div>
    </footer>
  );
};

export default Footer;
